import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/financiar/shopify-payouts`;
const lei = (v) => `${Number(v || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei`;
const STATUS = {
  PAID: ["Depusă", "bg-zinc-800 text-zinc-200"], SCHEDULED: ["Programată", "bg-amber-400/20 text-amber-300"],
  IN_TRANSIT: ["În tranzit", "bg-sky-400/20 text-sky-300"], FAILED: ["Eșuată", "bg-rose-500/20 text-rose-300"], CANCELED: ["Anulată", "bg-rose-500/20 text-rose-300"],
};

const PayoutRow = ({ p }) => {
  const [open, setOpen] = useState(false);
  const [label, cls] = STATUS[p.status] || [p.status, "bg-zinc-800 text-zinc-300"];
  return (
    <Fragment>
      <tr className="cursor-pointer border-t border-zinc-800 hover:bg-zinc-900/60" onClick={() => setOpen((v) => !v)} data-testid={`shopify-payout-${p.legacy_id}`}>
        <td className="px-3 py-2 text-zinc-400">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="px-3 py-2 text-zinc-200">{p.date}</td>
        <td className="px-3 py-2 font-mono text-xs text-zinc-400">#{p.legacy_id}</td>
        <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs ${cls}`}>{label}</span></td>
        <td className="px-3 py-2 text-right text-zinc-300">{p.orders.length}</td>
        <td className="px-3 py-2 text-right text-zinc-300">{lei(p.gross)}</td>
        <td className="px-3 py-2 text-right text-rose-300">−{lei(Math.abs(p.fee))}</td>
        <td className="px-3 py-2 text-right font-semibold text-emerald-400" data-testid={`shopify-payout-net-${p.legacy_id}`}>{lei(p.net)}</td>
      </tr>
      {open && p.orders.map((o) => (
        <tr key={o.order_gid} className="bg-zinc-900/40 text-xs" data-testid={`shopify-payout-order-${o.order_name.replace("#", "")}`}>
          <td />
          <td className="px-3 py-1.5 text-zinc-500">{o.order_date || "—"}</td>
          <td className="px-3 py-1.5 font-medium text-zinc-200">{o.order_name}</td>
          <td className="px-3 py-1.5 text-zinc-400">{o.customer || "—"}</td>
          <td className="px-3 py-1.5 text-right text-zinc-500">{o.types.includes("REFUND") ? "cu retur" : ""}</td>
          <td className="px-3 py-1.5 text-right text-zinc-300">{lei(o.amount)}</td>
          <td className="px-3 py-1.5 text-right text-rose-300">−{lei(Math.abs(o.fee))}</td>
          <td className="px-3 py-1.5 text-right text-emerald-300">{lei(o.net)}</td>
        </tr>
      ))}
      {open && p.other.map((t, i) => (
        <tr key={`o${i}`} className="bg-zinc-900/40 text-xs text-zinc-400">
          <td /><td className="px-3 py-1.5">{t.date?.slice(0, 10)}</td><td className="px-3 py-1.5" colSpan={3}>{t.type} (fără comandă)</td>
          <td className="px-3 py-1.5 text-right">{lei(t.amount)}</td><td className="px-3 py-1.5 text-right">−{lei(Math.abs(t.fee))}</td><td className="px-3 py-1.5 text-right">{lei(t.net)}</td>
        </tr>
      ))}
    </Fragment>
  );
};

export const ShopifyPayouts = ({ year, month, onSynced }) => {
  const [data, setData] = useState({ items: [], totals: {} });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(API, { params: { year, month } }).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || e.message)), [year, month]);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sync`, null, { params: { force: true } });
      toast.success(`Shopify: ${r.data.payouts} plăți · ${r.data.orders_marked} comenzi noi marcate încasate`);
      load(); onSynced?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const t = data.totals || {};
  return (
    <div className="space-y-3" data-testid="shopify-payouts-view">
      <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-400">
        <span data-testid="shopify-payouts-totals">{t.count || 0} plăți · {t.orders || 0} comenzi · brut {lei(t.gross)} · comision <span className="text-rose-300">−{lei(Math.abs(t.fee || 0))}</span> · net <span className="font-semibold text-emerald-400">{lei(t.net)}</span></span>
        <span className="ml-auto">Ultima sincronizare: {data.last_sync ? new Date(data.last_sync).toLocaleString("ro-RO") : "—"}</span>
        <button onClick={sync} disabled={busy} data-testid="shopify-payouts-sync-btn" className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Se sincronizează..." : "Sincronizează Shopify"}
        </button>
      </div>
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="w-8" /><th className="px-3 py-2 text-left">Dată plată</th><th className="px-3 py-2 text-left">Plată / Comandă</th><th className="px-3 py-2 text-left">Stare / Client</th>
              <th className="px-3 py-2 text-right">Comenzi</th><th className="px-3 py-2 text-right">Brut</th><th className="px-3 py-2 text-right">Comision</th><th className="px-3 py-2 text-right">Net</th></tr>
          </thead>
          <tbody>
            {data.items.length ? data.items.map((p) => <PayoutRow key={p.id} p={p} />)
              : <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500" data-testid="shopify-payouts-empty">Nicio plată Shopify în luna aleasă.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
